import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import TitlePage from 'components/atoms/title-page'
import CsvUploadCard from 'components/organisms/csv-upload-card'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useHistory } from 'react-router'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, CustomInput, Row, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const PRODUCT_COST_SAMPLE_CSV = [
  'sku,shipment_cost,authentication_cost',
  'BKR-HR01-010125-0001,150000,250000',
  'BKR-HR01-010125-0002,0,250000',
  'BKR-HR01-010125-0003,175000,6000000'
].join('\n')
const PRODUCT_COST_SAMPLE_CSV_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(PRODUCT_COST_SAMPLE_CSV)}`

const COST_IMPORT_STATUS_COLOR = {
  dry_run   : 'info',
  updated   : 'success',
  skipped   : 'warning',
  not_found : 'danger',
  invalid   : 'danger'
}

const formatCostValue = (value) => {
  if (value === undefined || value === null || value === '') return '-'

  const number = Number(value)
  if (!Number.isFinite(number)) return value

  return new Intl.NumberFormat('id-ID').format(number)
}

const toNumber = (value) => {
  const number = Number(value || 0)

  return Number.isFinite(number) ? number : 0
}

const downloadCsvBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([ blob ], { type: 'text/csv;charset=utf-8;' }))
  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.parentNode.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const InventoryCostCsvImportPage = ({ pageUtils }) => {
  const [ costCsvFile, setCostCsvFile ]               = useState(null)
  const [ costCsvPreview, setCostCsvPreview ]         = useState(null)
  const [ isUploadingCostCsv, setIsUploadingCostCsv ] = useState(false)
  const [ isImportingCostCsv, setIsImportingCostCsv ] = useState(false)
  const [ isConfirmCostModalOpen, setIsConfirmCostModalOpen ] = useState(false)
  const [ shouldOverwriteCost, setShouldOverwriteCost ]       = useState(true)

  const history = useHistory()
  const costCsvRows = costCsvPreview?.rows || []
  const costCsvSummary = costCsvPreview?.summary || {}
  const invalidCostCsvCount = toNumber(costCsvSummary.invalid) + toNumber(costCsvSummary.not_found)

  const handleApiError = (error) => {
    const data = error?.response?.data

    if (data instanceof Blob) {
      data.text().then((text) => {
        try {
          pageUtils.setApiErrorMsg(JSON.parse(text))
        } catch (_) {
          pageUtils.setAlertMsg(text || 'Upload cost CSV failed.', ALERT_TYPES.DANGER)
        }
      })
      return
    }

    pageUtils.setApiErrorMsg(data || { message: 'Upload cost CSV failed.' })
  }

  const handleUploadCostCsv = (file) => {
    setIsUploadingCostCsv(true)
    setCostCsvFile(file)

    InventoriesApiV2.previewCostCsv(file, { force: shouldOverwriteCost }).then((response) => {
      setCostCsvPreview(response.data)
      pageUtils.setAlertMsg('Cost CSV uploaded. Please check preview table before Confirm Update.', ALERT_TYPES.SUCCESS)
    }).catch(handleApiError)
      .finally(() => setIsUploadingCostCsv(false))
  }

  const handleOverwriteCostChange = (event) => {
    setShouldOverwriteCost(event.target.checked)
    setCostCsvFile(null)
    setCostCsvPreview(null)
  }

  const handleBackToInventoryList = () => {
    history.push({
      pathname: window.location.pathname.replace(/\/import-cost-csv\/?$/, '/list-page')
    })
  }

  const handleConfirmImportCostCsv = () => {
    if (!costCsvFile) {
      pageUtils.setAlertMsg('Upload CSV terlebih dahulu sebelum confirm update.', ALERT_TYPES.WARNING)
      return
    }

    setIsImportingCostCsv(true)
    InventoriesApiV2.importCostCsv(costCsvFile, { force: shouldOverwriteCost }).then((response) => {
      const filename = `product_cost_import_report_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')}.csv`

      downloadCsvBlob(response.data, filename)
      setIsConfirmCostModalOpen(false)
      setCostCsvFile(null)
      setCostCsvPreview(null)
      pageUtils.setAlertMsg('Cost CSV has been updated. Report CSV downloaded automatically.', ALERT_TYPES.SUCCESS)
    }).catch((error) => {
      setIsConfirmCostModalOpen(false)
      handleApiError(error)
    }).finally(() => setIsImportingCostCsv(false))
  }

  return (
    <>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <TitlePage mainTitle={ 'Finance Upload Cost CSV' } />
        <Button color='secondary' outline onClick={ handleBackToInventoryList }>
          { 'Back to Inventory List' }
        </Button>
      </div>

      <Card className='shadow-sm border-0'>
        <CardHeader className='bg-white'>
          <Row className='align-items-center'>
            <Col md={ 8 }>
              <h3 className='mb-1'>{ 'Finance Upload Cost CSV' }</h3>
              <p className='text-muted mb-0'>
                { 'Upload shipment cost dan authentication cost, cek preview table, lalu confirm update. Report CSV akan auto-download setelah update.' }
              </p>
            </Col>
            <Col md={ 4 } className='text-md-right mt-3 mt-md-0'>
              <a
                className='btn btn-outline-primary'
                href={ PRODUCT_COST_SAMPLE_CSV_URL }
                download='finance-upload-cost-template.csv'
              >
                { 'Download CSV Template' }
              </a>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <Alert color='info'>
            <strong>{ 'Required headers: ' }</strong>
            <code>{ 'sku' }</code>
            { ', ' }
            <code>{ 'shipment_cost' }</code>
            { ', ' }
            <code>{ 'authentication_cost' }</code>
            <div className='mt-1'>
              { 'Accepted format: 150000, 150.000, atau Rp 150.000. Default mengikuti Old Dashboard: overwrite existing values aktif.' }
            </div>
          </Alert>

          <Row>
            <Col lg={ 5 }>
              <div className='mb-3'>
                <CustomInput
                  id='inventory-cost-csv-force'
                  type='checkbox'
                  label='Overwrite existing cost values (recommended)'
                  checked={ shouldOverwriteCost }
                  onChange={ handleOverwriteCostChange }
                />
              </div>

              <div className='table-responsive mb-3'>
                <Table bordered size='sm' className='mb-0'>
                  <thead>
                    <tr>
                      <th>{ 'Header' }</th>
                      <th>{ 'Required?' }</th>
                      <th>{ 'Example' }</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>{ 'sku' }</code></td>
                      <td>{ 'Yes' }</td>
                      <td><code>{ 'BKR-HR01-010125-0001' }</code></td>
                    </tr>
                    <tr>
                      <td><code>{ 'shipment_cost' }</code></td>
                      <td>{ 'No' }</td>
                      <td><code>{ '150000' }</code></td>
                    </tr>
                    <tr>
                      <td><code>{ 'authentication_cost' }</code></td>
                      <td>{ 'No' }</td>
                      <td><code>{ '250000' }</code></td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <pre className='bg-light border rounded p-3 mb-3 small'>
                { PRODUCT_COST_SAMPLE_CSV }
              </pre>

              <CsvUploadCard
                label='Upload Cost CSV / XLSX'
                onFileUpload={ handleUploadCostCsv }
                isUploading={ isUploadingCostCsv }
              />
            </Col>

            <Col lg={ 7 }>
              <Row className='mb-3'>
                <Col sm={ 3 }>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Total' }</div>
                    <h3 className='mb-0'>{ costCsvSummary.total || 0 }</h3>
                  </div>
                </Col>
                <Col sm={ 3 } className='mt-3 mt-sm-0'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Will Update' }</div>
                    <h3 className='mb-0'>{ costCsvSummary.updated || 0 }</h3>
                  </div>
                </Col>
                <Col sm={ 3 } className='mt-3 mt-sm-0'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Skipped' }</div>
                    <h3 className='mb-0'>{ costCsvSummary.skipped || 0 }</h3>
                  </div>
                </Col>
                <Col sm={ 3 } className='mt-3 mt-sm-0'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='text-muted small'>{ 'Invalid' }</div>
                    <h3 className='mb-0'>{ invalidCostCsvCount }</h3>
                  </div>
                </Col>
              </Row>

              <div className='d-flex justify-content-between align-items-center mb-2'>
                <h4 className='mb-0'>{ 'CSV Preview Result' }</h4>
                <Button
                  color='success'
                  disabled={ !costCsvFile || costCsvRows.length === 0 || isImportingCostCsv }
                  onClick={ () => setIsConfirmCostModalOpen(true) }
                >
                  { isImportingCostCsv ? 'Updating...' : 'Confirm Update Cost' }
                </Button>
              </div>

              <div className='table-responsive' style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <Table bordered hover size='sm' className='mb-0'>
                  <thead className='thead-light'>
                    <tr>
                      <th>{ 'SKU' }</th>
                      <th>{ 'Status' }</th>
                      <th>{ 'Message' }</th>
                      <th>{ 'Shipment Cost' }</th>
                      <th>{ 'Authentication Cost' }</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      costCsvRows.length > 0 ? costCsvRows.map((row, index) => (
                        <tr key={ `${row.sku}-${index}` }>
                          <td>{ row.sku || '-' }</td>
                          <td>
                            <Badge color={ COST_IMPORT_STATUS_COLOR[row.status] || 'secondary' }>
                              { row.status || '-' }
                            </Badge>
                          </td>
                          <td>{ row.message || '-' }</td>
                          <td>{ formatCostValue(row.shipment_cost) }</td>
                          <td>{ formatCostValue(row.authentication_cost) }</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={ 5 } className='text-center text-muted py-4'>
                            { 'Upload CSV untuk melihat preview hasil import di sini.' }
                          </td>
                        </tr>
                      )
                    }
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <ConfirmationModal
        isOpen={ isConfirmCostModalOpen }
        toggle={ () => !isImportingCostCsv && setIsConfirmCostModalOpen(false) }
        title='Confirm Update Product Cost'
        handleConfirmation={ handleConfirmImportCostCsv }
      >
        <div>
          <h4>{ 'Update product cost from uploaded CSV?' }</h4>
          <p className='mb-1'>
            { `Total rows: ${costCsvSummary.total || 0}. Rows to update: ${costCsvSummary.updated || 0}.` }
          </p>
          <p className='text-muted mb-0'>
            { 'After update, report CSV akan otomatis ter-download.' }
          </p>
        </div>
      </ConfirmationModal>
    </>
  )
}

InventoryCostCsvImportPage.propTypes = propTypes

export default InventoryCostCsvImportPage
